import React from "react";

import { JsonEditor } from "@/common/JsonEditor";
import { jsonPayloadToDescriptions } from "@/common/graph/utils";
import { CSVReadOnlyPreview } from "@/pages/models/release/Technical/MappingV2/CSVPreview";
import { mapRequestData } from "@packages/commons";
import Tabs from "@imminently/decisively-ui/Tabs";
import { InteractionDataView } from "./InteractionDataView";

export interface RecordedCall {
  timestamp?: string | Date;
  payload?: any;
}

type CallTab = "data" | "mapped" | "payload";

interface EncodedCsvMappingInput {
  encodedData: string;
  csvText: string;
  mapping: any;
  mappingId: string;
}

const MAPPING_PREVIEW_CSV_ROWS = 200;

export const getCallData = (call: RecordedCall): any => {
  const payload = call.payload;
  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    Object.prototype.hasOwnProperty.call(payload, "data")
  ) {
    return payload.data ?? {};
  }
  return payload ?? {};
};

const stringifyJson = (value: any): string => JSON.stringify(value ?? {}, null, 2);

export const formatCallDataForEditor = (call: RecordedCall, graph: any): string => {
  const data = getCallData(call);
  try {
    const result = jsonPayloadToDescriptions({ data }, graph);
    return typeof result === "string" ? result : stringifyJson({ data });
  } catch {
    return stringifyJson({ data });
  }
};

export const formatCallPayloadForEditor = (call: RecordedCall): string => stringifyJson(call.payload ?? {});

const decodeBase64Utf8 = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const binary = atob(trimmed);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
};

const getEncodedString = (value: unknown): string | null => {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const entries = Object.entries(value);
  if (!entries.length) return null;
  const indexedCharacters = entries
    .map(([key, entryValue]) => {
      const index = Number(key);
      return Number.isInteger(index) && typeof entryValue === "string"
        ? { index, value: entryValue }
        : null;
    })
    .filter((entry): entry is { index: number; value: string } => Boolean(entry))
    .sort((a, b) => a.index - b.index);

  if (indexedCharacters.length !== entries.length) return null;
  return indexedCharacters.map((entry) => entry.value).join("");
};

const findReleaseMapping = (release: any, mappingId: unknown): any | null => {
  const normalizedMappingId = typeof mappingId === "string" ? mappingId.trim() : "";
  if (!normalizedMappingId || !Array.isArray(release?.mapping)) return null;
  return release.mapping.find((mapping: any) => mapping?.id === normalizedMappingId || mapping?.name === normalizedMappingId) ?? null;
};

const getEncodedCsvMappingInput = (call: RecordedCall, release: any): EncodedCsvMappingInput | null => {
  const payload = call.payload;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const mapping = findReleaseMapping(release, (payload as any).mapping);
  if (!mapping || String(mapping.mode || "").toLowerCase() !== "csv") return null;
  const encodedData = getEncodedString((payload as any).data);
  if (!encodedData) return null;
  const csvText = decodeBase64Utf8(encodedData);
  if (!csvText) return null;
  return {
    encodedData,
    csvText,
    mapping,
    mappingId: String((payload as any).mapping),
  };
};

const useMappedCsvPreview = (
  csvMappingInput: EncodedCsvMappingInput | null,
  release: any,
  enabled: boolean,
): { mappedValue: string; isMapping: boolean } => {
  const [mappedValue, setMappedValue] = React.useState("{}");
  const [isMapping, setIsMapping] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!csvMappingInput || !enabled) {
      setMappedValue("{}");
      setIsMapping(false);
      return;
    }

    setIsMapping(true);
    mapRequestData(
      {
        ...csvMappingInput.mapping,
        mode: "csv",
        type: "request",
      },
      release,
      csvMappingInput.encodedData,
    )
      .then((mapped) => {
        if (!cancelled) {
          setMappedValue(stringifyJson(mapped));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setMappedValue(
            stringifyJson({
              error: error instanceof Error ? error.message : String(error),
            }),
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsMapping(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [csvMappingInput, enabled, release]);

  return { mappedValue, isMapping };
};

const formatTimestamp = (timestamp: RecordedCall["timestamp"]): string => {
  if (!timestamp) return "No timestamp";
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "No timestamp";
  return date.toISOString().replace("T", " ").slice(0, 19);
};

const CallTabs = ({
  call,
  graph,
  release,
  fullHeight = false,
}: {
  call: RecordedCall;
  graph: any;
  release?: any;
  fullHeight?: boolean;
}) => {
  const [activeTab, setActiveTab] = React.useState<CallTab>("data");
  const csvMappingInput = React.useMemo(() => getEncodedCsvMappingInput(call, release), [call, release]);
  const { mappedValue, isMapping } = useMappedCsvPreview(csvMappingInput, release, activeTab === "mapped");
  const callData = React.useMemo(() => getCallData(call), [call]);

  React.useEffect(() => {
    if (!csvMappingInput && activeTab === "mapped") {
      setActiveTab("data");
    }
  }, [activeTab, csvMappingInput]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => setActiveTab(value as CallTab)}
      className={`flex flex-col ${fullHeight ? "h-full" : ""}`}
    >
      <Tabs.List className="border-b border-border px-3 pt-2">
        <Tabs.Trigger value="data">Data</Tabs.Trigger>
        {csvMappingInput ? <Tabs.Trigger value="mapped">Mapped</Tabs.Trigger> : null}
        <Tabs.Trigger value="payload">Payload</Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content
        value="data"
        className={`min-h-0 flex-1 p-0 ${fullHeight ? "h-full" : ""}`}
      >
        {csvMappingInput ? (
          <div className={fullHeight ? "h-full min-h-[520px]" : "min-h-[520px]"}>
            <CSVReadOnlyPreview
              csvData={csvMappingInput.csvText}
              maxRows={MAPPING_PREVIEW_CSV_ROWS}
              title={`Decoded CSV: ${csvMappingInput.mapping?.name ?? csvMappingInput.mappingId}`}
            />
          </div>
        ) : (
          <InteractionDataView
            data={callData}
            rawData={{ data: callData }}
            graph={graph}
            showRawToggle={false}
          />
        )}
      </Tabs.Content>
      {csvMappingInput ? (
        <Tabs.Content
          value="mapped"
          className={`min-h-0 flex-1 p-0 ${fullHeight ? "h-full" : ""}`}
        >
          <JsonEditor
            className={fullHeight ? "h-full min-h-[520px]" : "min-h-[520px]"}
            value={isMapping ? stringifyJson({ status: "Mapping CSV..." }) : mappedValue}
            disabled
            withCopy
          />
        </Tabs.Content>
      ) : null}
      <Tabs.Content
        value="payload"
        className={`min-h-0 flex-1 p-0 ${fullHeight ? "h-full" : ""}`}
      >
        <JsonEditor
          className={fullHeight ? "h-full min-h-[520px]" : "min-h-[520px]"}
          value={formatCallPayloadForEditor(call)}
          disabled
          withCopy
        />
      </Tabs.Content>
    </Tabs>
  );
};

export const CallsViewer = ({ calls, graph, release }: { calls: RecordedCall[]; graph: any; release?: any }) => {
  if (!calls.length) {
    return <p className="text-sm text-muted-foreground">No calls recorded for this interaction.</p>;
  }

  if (calls.length === 1) {
    return (
      <div
        className="h-full"
        data-testid="single-call-viewer"
      >
        <CallTabs
          call={calls[0]}
          graph={graph}
          release={release}
          fullHeight
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col-reverse gap-4">
      {calls.map((call, index) => (
        <div
          key={`${String(call.timestamp ?? "")}-${index}`}
          className="overflow-hidden rounded border border-border"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <h3 className="text-sm font-medium">Call {index + 1}</h3>
            <span className="text-xs text-muted-foreground">{formatTimestamp(call.timestamp)}</span>
          </div>
          <CallTabs
            call={call}
            graph={graph}
            release={release}
          />
        </div>
      ))}
    </div>
  );
};
